/**
 * The Central widget on the institutional site (D427 W-EMBED-SCRIPT). The site installs the widget exactly as a partner
 * would — one `<script src="/widget.js" data-key="vw_pk_…" async>` — but only when its runtime config
 * (`/site-config.json`, written by `scripts/gen-site-config.mjs` from `VELLO_SITE_WIDGET_KEY` / `VELLO_SITE_WIDGET_API`)
 * carries a key: the key is created at the staging apply (`POST /v1/config/widgets`, allowing the site's origin), so a
 * site built without one simply has no widget. The script is same-origin (`/widget.js`, served by the site's nginx).
 *
 * Pure: it validates the config and returns the tag's attributes; `web/main.tsx` adds the tag.
 */

/** The optional `widget` block of the site's runtime config. */
export interface SiteWidgetConfig {
  /** The Vello-site embed's public key (`vw_pk_…`, ADR-0067 — public by design). */
  readonly key: string;
  /** The API origin; absent → the script's default (`https://api.usevello.app`). */
  readonly api?: string;
}

export interface WidgetScriptAttributes {
  readonly src: string;
  readonly 'data-key': string;
  readonly 'data-api'?: string;
}

const KEY_SHAPE = /^vw_pk_[A-Za-z0-9_-]{20,128}$/;
const ORIGIN_SHAPE = /^https?:\/\/[^/\s?#]+\/?$/;

/** The attributes of the widget's `<script>` tag, or undefined when the config names no (valid) key. */
export function widgetScriptAttributes(widget: unknown): WidgetScriptAttributes | undefined {
  if (typeof widget !== 'object' || widget === null) return undefined;
  const { key, api } = widget as { key?: unknown; api?: unknown };
  if (typeof key !== 'string' || !KEY_SHAPE.test(key)) return undefined;
  const origin = typeof api === 'string' && ORIGIN_SHAPE.test(api) ? api.replace(/\/$/, '') : undefined;
  return { src: '/widget.js', 'data-key': key, ...(origin !== undefined ? { 'data-api': origin } : {}) };
}
